/**
 * The dashboard shell: a role-aware sidebar, a sticky topbar with the theme toggle and
 * the signed-in operator, and the routed page in between. The menu is filtered by the
 * admin's permissions (from the mirrored `adminCan`), so a support operator never sees
 * a control they cannot use — the same map the API enforces on.
 */
import { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { USING_MOCK } from '../api/config.js';
import { adminCanAny, ROLE_LABELS } from '../contracts/permissions.js';
import { useAuth } from '../auth/AuthContext.js';
import { useTheme } from '../hooks/useTheme.js';
import { IconLogout, IconMenu, IconMoon, IconSun } from './icons.js';
import { NAV_SECTIONS, PAGE_TITLES } from './nav.js';
import { useBrand } from '../branding/BrandProvider.js';

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join('');
}

export function Layout(): JSX.Element {
  const { session, logout } = useAuth();
  const brand = useBrand();
  const { theme, toggle } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const me = session?.me;
  const role = me?.role ?? 'support';

  // `adminCanAny` rather than `adminCan`, because a nav entry may name a list: the pages
  // that carry two grants (gift cards is compliance's review queue and finance's rate
  // catalogue on one screen) must not lose their entry to the other role's permission.
  //
  // A section whose every entry was filtered out is dropped rather than rendered as a
  // heading over nothing — a support operator seeing "Platform" with no links under it
  // would read it as a page that failed to load, not as a section they cannot use.
  const sections = useMemo(
    () =>
      NAV_SECTIONS.map((section) => ({
        ...section,
        items: section.items.filter((item) => adminCanAny(role, item.permission)),
      })).filter((section) => section.items.length > 0),
    [role],
  );

  const title = PAGE_TITLES[location.pathname] ?? brand.platformName;

  return (
    <div className="shell">
      {menuOpen && <div className="scrim" onClick={() => setMenuOpen(false)} />}
      <aside className={`sidebar${menuOpen ? ' sidebar--open' : ''}`}>
        <div className="sidebar__brand">
          <div className="brandmark">
            {/* The uploaded logo when there is one, else the platform's own initial. Never a
                hardcoded letter: a console showing "G" for Acme Money is the same mistake as
                the app naming the wrong company in its fraud warnings. */}
            <BrandLogo url={brand.logoUrl} platformName={brand.platformName} />
            <span>
              {brand.platformName}
              <div className="brandmark__sub">Operator Console</div>
            </span>
          </div>
        </div>

        <nav className="sidebar__nav" aria-label="Console sections">
          {sections.map((section) => (
            <div className="sidebar__group" key={section.id}>
              {section.label !== '' && <div className="sidebar__section">{section.label}</div>}
              {section.items.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end ?? false}
                    onClick={() => setMenuOpen(false)}
                    className={({ isActive }) => `navlink${isActive ? ' navlink--active' : ''}`}
                  >
                    <span className="navlink__icon">
                      <Icon size={18} />
                    </span>
                    {item.label}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="sidebar__foot">
          <div className="env-pill">
            <span
              className="badge__dot"
              style={{ background: USING_MOCK ? 'var(--amber-500)' : 'var(--green-500)' }}
            />
            {USING_MOCK ? 'Demo data' : 'Live'}
          </div>
        </div>
      </aside>

      <div className="main">
        <header className="topbar">
          {/* The bar is full width so its border and blur reach both edges; its contents are
              held to the same measure as the page body below, so the title and the first
              heading on the page share a left edge on every screen. */}
          <div className="topbar__inner">
            <button
              className="iconbtn topbar__menu-btn"
              aria-label="Toggle menu"
              onClick={() => setMenuOpen((o) => !o)}
            >
              <IconMenu size={18} />
            </button>
            <div className="topbar__title">{title}</div>
            <div className="topbar__spacer" />

            <button
              className="iconbtn"
              aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
              onClick={toggle}
            >
              {theme === 'dark' ? <IconSun size={18} /> : <IconMoon size={18} />}
            </button>

            {me !== undefined && (
              <div className="usermenu">
                <div className="avatar" title={me.email}>
                  {initialsOf(me.name)}
                </div>
                <div className="usermenu__who hide-sm">
                  <div className="usermenu__name">{me.name}</div>
                  <div className="usermenu__role">{ROLE_LABELS[me.role]}</div>
                </div>
                <button
                  className="iconbtn"
                  aria-label="Sign out"
                  onClick={() => {
                    logout();
                    navigate('/login', { replace: true });
                  }}
                >
                  <IconLogout size={18} />
                </button>
              </div>
            )}
          </div>
        </header>

        <main className="content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/**
 * The lettermark shown when no logo has been uploaded.
 *
 * Strips anything that is not a letter or digit, so "Acme Money" gives "A" rather than
 * whatever punctuation the name starts with, and a name that is entirely punctuation still
 * yields something rather than an empty box.
 */
function initialOf(name: string): string {
  const cleaned = name.replace(/[^A-Za-z0-9]/g, '');
  return (cleaned[0] ?? '?').toUpperCase();
}

/**
 * The operator's logo in the sidebar, falling back to the lettermark when it will not load.
 *
 * Deliberately quieter than the Brand assets page, which draws a red panel naming the URL that
 * failed. The difference is who is looking and at what: this is page chrome on every screen in
 * the console, and an error box wedged into the sidebar on the transactions page tells an
 * operator their transactions are broken. Brand assets is the screen someone opens *to look at
 * the logo*, so that is where the failure is explained, and this stays a clean lettermark.
 *
 * What it must not do is what it did before: render a bare `<img>` whose failure is a
 * broken-picture glyph — the console's own chrome advertising that the console is broken, on
 * every page, with no way to find out why.
 *
 * The `title` is the one thread back to the explanation, for whoever hovers it.
 */
function BrandLogo({
  url,
  platformName,
}: {
  readonly url: string | null;
  readonly platformName: string;
}): JSX.Element {
  const [failed, setFailed] = useState(false);

  // A re-resolved brand is a fresh attempt; without this a logo fixed on the server stayed
  // hidden until the operator reloaded the console.
  useEffect(() => {
    setFailed(false);
  }, [url]);

  if (url === null || failed) {
    return (
      <span
        className="brandmark__glyph"
        title={
          failed
            ? 'The operator logo did not load. Brand assets shows what went wrong.'
            : undefined
        }
      >
        {initialOf(platformName)}
      </span>
    );
  }

  return (
    <img
      src={url}
      alt=""
      className="brandmark__logo"
      onError={() => setFailed(true)}
    />
  );
}
